import React, { FunctionComponent, useEffect, useRef } from 'react'
import c3, { ChartAPI, ChartConfiguration } from 'c3'
import { CustomerSummary } from './CustomerSummaries'
import { compact } from './hubBrand'
import { LIFECYCLE_STAGES, LifecycleStage, stageOrDefault } from './lifecycle'
import { PH_LIGHT } from './phosphor'
import { prefersReducedMotion } from './countUp'
import { useChartResize } from '../Visualizations/useChartResize'
import '../Visualizations/chartTooltip.css'
import styles from './HubMetricCharts.module.css'

/*
 * The small charts on the landing page's metric tiles, one kind each: the team's devices by brand (donut), pages
 * printed each month (area), the share of customers at high risk (gauge) and fleet value by lifecycle stage
 * (bars). Kept minimal: no legends, axes, gridlines or points; hovering still shows each value.
 */

const CHART_HEIGHT = 72
const TILE_HEIGHT = 104 // area, bars, donut and gauge run the tile's full height (its min-height)

/* muted, like the brief thumbnails: soft teals and limes, light enough to sit quietly beside the numbers */
const MUTED_COLOURS = ['#6cc3b1', '#a9d46f', '#a8dccf', '#cfe6ae', '#8fb8c9']
const MUTED_LINE = MUTED_COLOURS[0]

/* as in the customer table: its High renewal risk red, and its lifecycle stage dots (Serve & upsell's dot is half
   Serve teal, half Upsell blue: its bar takes the teal) */
const HIGH_RISK_COLOUR = '#bf2348'
const STAGE_COLOURS: Record<LifecycleStage, string> = {
  trial: '#ffb101',
  onboarding: '#039966',
  upsell: '#32c2af',
  retention: '#5c6ac4',
  expired: '#bf2348',
}
/* the charts grow in from nothing when they first draw: the bars grow, the donut and gauge fill (the area draws
   itself across instead, left to right: see .reveal) */
const GROW_MS = 900
const GROW_DELAY_MS = 150

const PEAK = 0.65 // area and bars rise to about two-thirds of the tile, leaving it room above them

const HIGH_RISK = ['High', 'Very High', 'Extreme']

export const isHighRisk = (summary: CustomerSummary): boolean => HIGH_RISK.includes(summary.risk ?? '')

export interface StageValue {
  stage: LifecycleStage
  label: string
  value: number
}

/* every customer's estimated fleet value, added up by lifecycle stage (in stage order, stages with customers) */
export const teamValueByStage = (summaries: CustomerSummary[]): StageValue[] => {
  const totals = new Map<LifecycleStage, number>()
  summaries.forEach(s => {
    const stage = stageOrDefault(s.lifecycleStage)
    totals.set(stage, (totals.get(stage) ?? 0) + (s.estimatedFleetValue ?? 0))
  })
  return LIFECYCLE_STAGES.filter(stage => totals.has(stage.value)).map(stage => ({
    stage: stage.value,
    label: stage.label,
    value: totals.get(stage.value) ?? 0,
  }))
}

export interface MakeupSlice {
  label: string
  count: number
}

export interface TrendPoint {
  period: string
  value: number
}

const MAX_SLICES = 5 // the biggest brands; the rest are grouped as Other
const MAX_MONTHS = 12

/* every customer's devices by brand, added up: biggest first, the smallest grouped as Other */
export const teamDeviceMakeup = (summaries: CustomerSummary[]): MakeupSlice[] => {
  const totals = new Map<string, number>()
  summaries.forEach(s =>
    (s.deviceMakeup ?? []).forEach(({ label, count }) => {
      if (count > 0) totals.set(label, (totals.get(label) ?? 0) + count)
    })
  )
  const slices = Array.from(totals, ([label, count]) => ({ label, count })).sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label)
  )
  if (slices.length <= MAX_SLICES) return slices
  const other = slices.slice(MAX_SLICES - 1).reduce((sum, slice) => sum + slice.count, 0)
  return [...slices.slice(0, MAX_SLICES - 1), { label: 'Other', count: other }]
}

/* every customer's pages per month, added up, for the months with data (the latest 12) */
export const teamVolumeTrend = (summaries: CustomerSummary[]): TrendPoint[] => {
  const totals = new Map<string, number>()
  summaries.forEach(s =>
    (s.printVolumeTrend ?? []).forEach(point => {
      if (point.hasData) totals.set(point.period, (totals.get(point.period) ?? 0) + point.value)
    })
  )
  return Array.from(totals, ([period, value]) => ({ period, value }))
    .sort((a, b) => a.period.localeCompare(b.period))
    .slice(-MAX_MONTHS)
}

/* a c3 chart stripped to its marks, sized to the tile and following its width */
const MiniChart: FunctionComponent<{
  label: string
  config: Omit<ChartConfiguration, 'bindto' | 'size'>
  /** a round chart (donut, gauge): kept small at the tile's right, rather than spread across it */
  round?: boolean
  /** a gauge: c3 sits its half circle on the chart's bottom edge, so it's lifted to centre with the donut */
  gauge?: boolean
  /** an area or bar chart: runs to the tile's top, right and bottom edges */
  bleed?: boolean
  /** drawn across from left to right as it first appears, rather than growing up from 0 */
  reveal?: boolean
}> = ({ label, config, round = false, bleed = false, gauge = false, reveal = false }) => {
  const height = bleed || round ? TILE_HEIGHT : CHART_HEIGHT
  const elementRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<ChartAPI | null>(null)
  // redraw when what's drawn changes (the formatters and colour functions follow from the same data)
  const configKey = JSON.stringify(config)
  useChartResize(elementRef, chartRef, true, height)

  useEffect(() => {
    if (!elementRef.current) return undefined
    // drawn first with every value at 0, then given its values, which c3 animates to (unless motion is reduced)
    const columns = config.data?.columns ?? []
    const grow = !reveal && !prefersReducedMotion() && columns.length > 0
    const chart = c3.generate({
      bindto: elementRef.current,
      size: { height },
      // c3 keeps 4px above the chart and 8px below it for a hidden x axis: a bleeding chart takes them back, and a
      // round one evens them out to 6px each, so it's as big as the tile allows with a little room around it
      padding: bleed
        ? { top: -4, right: 0, bottom: -8, left: 0 }
        : round
        ? { top: 2, right: 0, bottom: -2, left: 0 }
        : { top: 2, right: 2, bottom: 2, left: 2 },
      legend: { show: false },
      point: { show: false },
      axis: { x: { show: false }, y: { show: false } },
      resize: { auto: false }, // useChartResize follows the tile instead
      transition: { duration: GROW_MS },
      ...config,
      data: {
        ...config.data,
        columns: grow ? columns.map(([name, ...values]) => [name, ...values.map(() => 0)]) : columns,
      },
    })
    chartRef.current = chart
    const timer = grow ? window.setTimeout(() => chart.load({ columns }), GROW_DELAY_MS) : undefined
    return () => {
      window.clearTimeout(timer)
      chartRef.current = null
      chart.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- configKey stands in for config
  }, [configKey, height, bleed, round, reveal])

  return (
    <div
      ref={elementRef}
      className={[
        styles.chart,
        round && styles.round,
        bleed && styles.bleed,
        gauge && styles.gauge,
        reveal && styles.reveal,
        'chart-tooltip',
      ]
        .filter(Boolean)
        .join(' ')}
      role='img'
      aria-label={label}
    />
  )
}

const monthName = (period: string): string => {
  const date = new Date(`${period}T00:00:00Z`)
  return Number.isNaN(date.getTime())
    ? period
    : date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
}

export const DevicesChart: FunctionComponent<{ slices: MakeupSlice[] }> = ({ slices }) => (
  <MiniChart
    label={`Devices by brand: ${slices.map(s => `${s.label} ${s.count.toLocaleString()}`).join(', ')}`}
    config={{
      data: {
        columns: slices.map(slice => [slice.label, slice.count]),
        type: 'donut',
        colors: Object.fromEntries(slices.map((slice, i) => [slice.label, MUTED_COLOURS[i % MUTED_COLOURS.length]])),
        order: null,
      },
      donut: { label: { show: false }, width: 18, title: '' },
      tooltip: { format: { value: value => Number(value).toLocaleString() } },
    }}
    round
  />
)

export const VolumeChart: FunctionComponent<{ points: TrendPoint[] }> = ({ points }) => {
  const values = points.map(p => p.value)
  const high = Math.max(...values)
  const low = Math.min(...values)
  // the line's swings stay readable (the axis doesn't start at 0) with its peak at about two-thirds height
  const floor = Math.max(0, low - (high - low || high))
  const ceiling = floor + (high - floor) / PEAK
  return (
    <MiniChart
      label={`Pages printed each month, ${monthName(points[0].period)} to ${monthName(
        points[points.length - 1].period
      )}: ${points[0].value.toLocaleString()} to ${points[points.length - 1].value.toLocaleString()}`}
      config={{
        data: {
          columns: [['Pages', ...points.map(p => p.value)]],
          type: 'area-spline',
          colors: { Pages: MUTED_LINE },
        },
        // an indexed axis with no padding, so the line runs from edge to edge (a category axis insets half a month)
        axis: {
          x: { show: false, padding: { left: 0, right: 0 } },
          y: { show: false, min: floor, max: ceiling, padding: { top: 0, bottom: 0 } },
        },
        tooltip: {
          format: {
            title: x => monthName(points[Number(x)]?.period ?? ''),
            value: value => Number(value).toLocaleString(),
          },
        },
      }}
      bleed
      reveal
    />
  )
}

export const RiskChart: FunctionComponent<{ flagged: number; customers: number }> = ({ flagged, customers }) => (
  <MiniChart
    label={`${flagged} of ${customers} customers at high risk`}
    config={{
      data: { columns: [['High risk', flagged]], type: 'gauge', colors: { 'High risk': HIGH_RISK_COLOUR } },
      gauge: {
        min: 0,
        max: Math.max(customers, 1),
        width: 18,
        label: { show: false, format: () => '' }, // no min, max or percentage: the number beside it says it
        expand: false,
      },
      tooltip: { format: { value: value => `${value} of ${customers} customers` } },
    }}
    round
    gauge
  />
)

export const ValueChart: FunctionComponent<{ stages: StageValue[] }> = ({ stages: allStages }) => {
  // a stage worth nothing gets no bar (and no gap where one would be)
  const stages = allStages.filter(s => s.value > 0)
  return (
    <MiniChart
      label={`Est. fleet value by lifecycle stage: ${stages
        .map(s => `${s.label} $${compact(Math.round(s.value))}`)
        .join(', ')}`}
      // a series per stage, side by side: each bar then has its own colour, and so does its tooltip's swatch (c3
      // colours a tooltip by series, so per-bar colours on one series showed the default blue there)
      config={{
        data: {
          columns: stages.map(s => [s.label, s.value]),
          type: 'bar',
          colors: Object.fromEntries(stages.map(s => [s.label, STAGE_COLOURS[s.stage]])),
          order: null,
        },
        bar: { width: { ratio: 1 } }, // no gaps between the bars
        axis: {
          x: { show: false, type: 'category', categories: [''] },
          y: {
            show: false,
            min: 0,
            max: Math.max(...stages.map(s => s.value), 1) / PEAK,
            padding: { top: 0, bottom: 0 },
          },
        },
        // the hovered bar only, on one line: swatch, stage, value
        tooltip: {
          grouped: false,
          format: { title: () => '', value: value => `$${compact(Math.round(Number(value)))}` },
        },
      }}
      bleed
    />
  )
}

/* the Circuit banner's icon (Phosphor, light) for a stat, at the banner's size */
export const StatIcon: FunctionComponent<{ name: 'devices' | 'risk' | 'value' }> = ({ name }) => (
  <svg viewBox='0 0 256 256' fill='currentColor' aria-hidden='true' className={styles.statIcon}>
    {PH_LIGHT[name].map(p => (
      <path key={p.d.slice(0, 24)} d={p.d} opacity={p.faded ? 0.2 : 1} />
    ))}
  </svg>
)

/* the four tiles while the team's numbers load: the same size and layout as the loaded tiles (icon, number and
   label, and each chart's shape), so nothing moves when they arrive */
type SkeletonChart = 'donut' | 'area' | 'gauge' | 'bars'

const SKELETON_TILES: { icon: boolean; chart: SkeletonChart }[] = [
  { icon: true, chart: 'donut' }, // Devices
  { icon: false, chart: 'area' }, // Print volume
  { icon: true, chart: 'gauge' }, // Risk flags
  { icon: false, chart: 'bars' }, // Est. fleet value
]

const SKELETON_BAR_HEIGHTS = [styles.skeletonBarTall, styles.skeletonBarTallest, styles.skeletonBarShort]

const ChartSkeleton: FunctionComponent<{ chart: SkeletonChart }> = ({ chart }) => {
  if (chart === 'donut' || chart === 'gauge') {
    return (
      <span className={`${styles.round} ${styles.skeletonRoundSlot}`}>
        <span className={`${styles.skeleton} ${chart === 'donut' ? styles.skeletonDonut : styles.skeletonGauge}`} />
      </span>
    )
  }
  return (
    <span className={`${styles.chart} ${styles.bleed} ${styles.skeletonBleedSlot}`}>
      {chart === 'area' ? (
        <span className={`${styles.skeleton} ${styles.skeletonArea}`} />
      ) : (
        SKELETON_BAR_HEIGHTS.map(height => (
          <span key={height} className={`${styles.skeleton} ${styles.skeletonBar} ${height}`} />
        ))
      )}
    </span>
  )
}

export const MetricTileSkeletons: FunctionComponent<{ gridClassName: string; tileClassName: string }> = ({
  gridClassName,
  tileClassName,
}) => (
  <div className={gridClassName} aria-busy='true' aria-label='Loading team metrics' data-testid='metric-skeletons'>
    {SKELETON_TILES.map(({ icon, chart }) => (
      <div key={chart} className={tileClassName} aria-hidden='true'>
        {icon && <span className={`${styles.skeleton} ${styles.skeletonIcon}`} />}
        <span className={styles.skeletonText}>
          <span className={`${styles.skeleton} ${styles.skeletonValue}`} />
          <span className={`${styles.skeleton} ${styles.skeletonLabel}`} />
        </span>
        <ChartSkeleton chart={chart} />
      </div>
    ))}
  </div>
)
